import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiBookOpen,
  FiBriefcase,
  FiCheckCircle,
  FiCreditCard,
  FiFileText,
  FiHelpCircle,
  FiLock,
  FiMail,
  FiX
} from 'react-icons/fi';
import '../landing/components/PricingPlans/pricing.css';
import '../styles/subscription-center.css';

const TABS = [
  { id: 'billing', label: 'Billing', icon: FiCreditCard },
  { id: 'invoice', label: 'Invoice', icon: FiFileText },
  { id: 'help', label: 'Help', icon: FiHelpCircle }
];

function formatMoney(cents) {
  const dollars = Math.round(cents) / 100;
  return dollars.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function mockInvoices() {
  const now = Date.now();
  return [
    { id: 'inv_0003', date: new Date(now - 7 * 86400000), amountCents: 9900, status: 'Paid', plan: 'Business' },
    { id: 'inv_0002', date: new Date(now - 37 * 86400000), amountCents: 2900, status: 'Paid', plan: 'Starter' },
    { id: 'inv_0001', date: new Date(now - 68 * 86400000), amountCents: 2900, status: 'Paid', plan: 'Starter' }
  ];
}

function BillingView({ onSubscribe, onContactSales }) {
  // Reuse the existing pricing layout/style by rendering the same section styling,
  // but provide subscription actions instead of the landing page links.
  return (
    <div className="subscription-center-billing">
      <div className="subscription-center-billing-lead">
        <span className="subscription-center-kicker">Plans</span>
        <h3>NovaBotX™ — Plans</h3>
        <p>Choose a plan to continue chat + admin access.</p>
      </div>

      <div className="pricing-cards subscription-center-pricing-grid">
        <div className="pricing-card">
          <h3>STARTER</h3>
          <p className="plan-desc">For small teams and pilots</p>
          <div className="price">
            $29<span>/month</span>
          </div>
          <p className="annual">(or $299 billed annually)</p>
          <ul>
            <li>1 tenant</li>
            <li>Up to 5 users</li>
            <li>50 MB document storage</li>
            <li>PDFs, images, text uploads</li>
            <li>Up to 5 website links</li>
            <li>Vision-enabled understanding</li>
            <li>Web & link crawling</li>
            <li>Smart question suggestions</li>
            <li>Source-aware answers</li>
            <li>Email-based user management</li>
            <li>2FA authentication</li>
            <li>Standard email support</li>
          </ul>
          <button type="button" className="btn primary subscription-center-cta" onClick={() => onSubscribe('Starter')}>
            Pay / Subscribe
          </button>
        </div>

        <div className="pricing-card highlight">
          <h3>BUSINESS</h3>
          <p className="plan-desc">For growing teams and departments</p>
          <div className="price">
            $99<span>/month</span>
          </div>
          <p className="annual">(or $999 billed annually)</p>
          <ul>
            <li>Up to 25 users</li>
            <li>250 MB document storage</li>
            <li>Unlimited document uploads</li>
            <li>Up to 50 website links</li>
            <li>Faster crawling & indexing</li>
            <li>Role-based access control</li>
            <li>Custom tenant branding</li>
            <li>Priority email support</li>
            <li>Admin dashboard & analytics</li>
            <li>Department-level deployment</li>
          </ul>
          <button type="button" className="btn primary subscription-center-cta" onClick={() => onSubscribe('Business')}>
            Pay / Subscribe
          </button>
        </div>

        <div className="pricing-card">
          <h3>ENTERPRISE</h3>
          <p className="plan-desc">For mission-critical use</p>
          <div className="price">Custom</div>
          <ul>
            <li>Unlimited users</li>
            <li>Custom storage limits</li>
            <li>Unlimited website crawling</li>
            <li>Advanced RBAC</li>
            <li>Tenant-level security policies</li>
            <li>SSO (SAML / OAuth)</li>
            <li>Audit logs & compliance</li>
            <li>API access & integrations</li>
            <li>SLA-backed uptime</li>
            <li>Dedicated onboarding</li>
          </ul>
          <button type="button" className="btn primary subscription-center-cta" onClick={onContactSales}>
            Contact Sales
          </button>
        </div>
      </div>

      <div className="subscription-center-hint">
        Prefer to review pricing on the landing page? Scroll to <span className="mono">#pricing</span>.
      </div>
    </div>
  );
}

function PaymentView({ planName, onBack, onDone }) {
  const [form, setForm] = useState({
    cardNumber: '',
    expiry: '',
    cvv: '',
    name: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { ok: boolean, message: string }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const validate = () => {
    if (!form.cardNumber.replace(/\s/g, '').match(/^\d{12,19}$/)) return 'Enter a valid card number.';
    if (!form.expiry.match(/^(0[1-9]|1[0-2])\/\d{2}$/)) return 'Expiry must be MM/YY.';
    if (!form.cvv.match(/^\d{3,4}$/)) return 'CVV must be 3–4 digits.';
    if (!form.name.trim()) return 'Name on card is required.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setResult({ ok: false, message: err });
      return;
    }
    setSubmitting(true);
    setResult(null);
    await new Promise((r) => setTimeout(r, 900));
    // UI-only mock rule: fail if card number ends with 0, succeed otherwise.
    const last = form.cardNumber.replace(/\s/g, '').slice(-1);
    const ok = last !== '0';
    setResult({
      ok,
      message: ok ? 'Payment successful.' : 'Payment failed. Please try another card.'
    });
    setSubmitting(false);
  };

  return (
    <div className="subscription-center-payment">
      <div className="subscription-center-payment-head">
        <button type="button" className="subscription-center-icon-btn" onClick={onBack} aria-label="Back">
          <FiArrowLeft />
        </button>
        <div>
          <span className="subscription-center-kicker">Checkout</span>
          <h3>Subscribe to {planName}</h3>
          <p><FiLock aria-hidden="true" className="subscription-center-inline-icon" /> Secure payment (UI-only demo — no real charges).</p>
        </div>
      </div>

      <form className="subscription-center-payment-form" onSubmit={handleSubmit}>
        <label>
          <span>Card number</span>
          <input
            name="cardNumber"
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="1234 5678 9012 3456"
            value={form.cardNumber}
            onChange={handleChange}
          />
        </label>
        <div className="subscription-center-payment-row">
          <label>
            <span>Expiry</span>
            <input
              name="expiry"
              inputMode="numeric"
              autoComplete="cc-exp"
              placeholder="MM/YY"
              value={form.expiry}
              onChange={handleChange}
            />
          </label>
          <label>
            <span>CVV</span>
            <input
              name="cvv"
              inputMode="numeric"
              autoComplete="cc-csc"
              placeholder="123"
              value={form.cvv}
              onChange={handleChange}
            />
          </label>
        </div>
        <label>
          <span>Name on card</span>
          <input
            name="name"
            autoComplete="cc-name"
            placeholder="Jane Doe"
            value={form.name}
            onChange={handleChange}
          />
        </label>

        {result && (
          <div className={`subscription-center-payment-result ${result.ok ? 'ok' : 'bad'}`} role="status">
            {result.ok ? <FiCheckCircle aria-hidden="true" /> : <FiAlertCircle aria-hidden="true" />}
            <span>{result.message}</span>
          </div>
        )}

        <div className="subscription-center-payment-actions">
          <button type="submit" className="subscription-center-primary-btn" disabled={submitting}>
            {submitting ? 'Processing…' : 'Submit Payment'}
          </button>
          <button type="button" className="subscription-center-secondary-btn" onClick={onDone}>
            Back to Chat
          </button>
        </div>
      </form>
    </div>
  );
}

function InvoiceView() {
  const invoices = useMemo(() => mockInvoices(), []);
  return (
    <div className="subscription-center-invoice">
      <div className="subscription-center-section-lead">
        <span className="subscription-center-kicker">Billing history</span>
        <h3>Invoices</h3>
        <p>Placeholder invoice history (hook up backend later).</p>
      </div>

      <div className="subscription-center-table">
        <div className="subscription-center-table-head">
          <span>Invoice</span>
          <span>Date</span>
          <span>Plan</span>
          <span>Amount</span>
          <span>Status</span>
        </div>
        {invoices.map((inv) => (
          <div key={inv.id} className="subscription-center-table-row">
            <span className="mono">{inv.id}</span>
            <span>{inv.date.toLocaleDateString()}</span>
            <span>{inv.plan}</span>
            <span>{formatMoney(inv.amountCents)}</span>
            <span className={`pill ${String(inv.status).toLowerCase()}`}>{inv.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HelpView() {
  return (
    <div className="subscription-center-help">
      <div className="subscription-center-section-lead">
        <span className="subscription-center-kicker">Support</span>
        <h3>Help</h3>
        <p>Reach support quickly or contact sales.</p>
      </div>

      <div className="subscription-center-help-grid">
        <div className="subscription-center-help-card">
          <span className="subscription-center-help-icon" aria-hidden="true"><FiMail /></span>
          <h4>Email support</h4>
          <p>Get help from our team.</p>
          <a className="subscription-center-link-btn" href="mailto:support@novalink-solutions.com">
            support@novalink-solutions.com
          </a>
        </div>
        <div className="subscription-center-help-card">
          <span className="subscription-center-help-icon is-green" aria-hidden="true"><FiBriefcase /></span>
          <h4>Contact sales</h4>
          <p>Need enterprise pricing or onboarding?</p>
          <a className="subscription-center-link-btn" href="mailto:sales@novalink-solutions.com">
            sales@novalink-solutions.com
          </a>
        </div>
        <div className="subscription-center-help-card">
          <span className="subscription-center-help-icon" aria-hidden="true"><FiBookOpen /></span>
          <h4>Documentation</h4>
          <p>Placeholder link for docs (add a real URL later).</p>
          <button type="button" className="subscription-center-link-btn" onClick={() => window.open('/#pricing', '_blank')}>
            Open docs / pricing
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SubscriptionCenterModal({ open, initialTab = 'billing', onClose }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [paymentPlan, setPaymentPlan] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setPaymentPlan(null);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const content = (
    <div
      className="subscription-center-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Subscription Center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="subscription-center-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="subscription-center-topbar">
          <div className="subscription-center-title">
            <span className="subscription-center-logo" aria-hidden="true"><FiCreditCard /></span>
            <span className="subscription-center-badge">Subscription</span>
            <span>Center</span>
          </div>
          <button type="button" className="subscription-center-icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        {!paymentPlan && (
          <div className="subscription-center-tabs" role="tablist" aria-label="Subscription tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`subscription-center-tab ${active ? 'active' : ''}`}
                  onClick={() => setTab(t.id)}
                  role="tab"
                  aria-selected={active}
                >
                  <Icon aria-hidden="true" />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="subscription-center-body">
          {paymentPlan ? (
            <PaymentView
              planName={paymentPlan}
              onBack={() => setPaymentPlan(null)}
              onDone={() => {
                onClose?.();
                navigate('/');
              }}
            />
          ) : tab === 'billing' ? (
            <BillingView
              onSubscribe={(planName) => setPaymentPlan(planName)}
              onContactSales={() => window.open('mailto:sales@novalink-solutions.com', '_blank')}
            />
          ) : tab === 'invoice' ? (
            <InvoiceView />
          ) : (
            <HelpView />
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

